import { z } from "zod";

const normalizeWebsiteUrl = (val: string): string => {
    if (val.length > 0 && !val.match(/^https?:\/\//)) {
        return `https://${val}`;
    }
    return val;
};

const isValidWebsiteUrl = (val: string): boolean => {
    try {
        // Require a real domain suffix (e.g. .com, .cz) — a bare URL parse alone accepts
        // single-label hosts like "https://as" as syntactically valid.
        return /\.[a-z]{2,}$/i.test(new URL(val).hostname);
    } catch {
        return false;
    }
};

export const schema = z
    .object({
        nickname: z.string().min(1, "forms.general-validation.required"),
        show_in_partner_catalog: z.boolean(),
        categories: z.array(z.string()),
        // The whole array is bound to a single "tags" Select, not per-item inputs — validating
        // per-array-item would attach the error at "websites.0", which react-hook-form-antd's
        // FormItem (bound to "websites") never renders. superRefine on the array itself attaches
        // the error to "websites" directly so it's actually visible under the field.
        websites: z.array(z.string().transform(normalizeWebsiteUrl)).superRefine((urls, ctx) => {
            if (urls.some((url) => !isValidWebsiteUrl(url))) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "forms.general-validation.url_invalid",
                });
            }
        }),
        web_types: z.array(z.string()),
        affiliate_propagation_info: z.string(),
        global_form_field_values: z.record(z.unknown()),
    })
    .superRefine((val, ctx) => {
        if (val.show_in_partner_catalog && val.categories.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["categories"],
                message: "forms.affiliate-propagation.fields.categories.required_when_shown_in_catalog",
            });
        }
    });

export type AccountProfileFormData = z.infer<typeof schema>;
